import io from "socket.io-client";

class SocketWorker {
  constructor(company_id, user_id) {
    if (SocketWorker.instance) {
      if (
        SocketWorker.instance.company_id !== company_id ||
        SocketWorker.instance.user_id !== user_id
      ) {
        SocketWorker.instance.disconnect();
        SocketWorker.instance = null;
      } else {
        return SocketWorker.instance;
      }
    }

    this.company_id = company_id;
    this.user_id = user_id;
    this.socket = null;
    this.eventListeners = {};
    this.isManualDisconnect = false;
    this.configureSocket();
    SocketWorker.instance = this;
    return SocketWorker.instance;
  }

  configureSocket() {
    const getCurrentToken = () => {
      const raw = localStorage.getItem("token");
      if (!raw || raw === "undefined" || raw === "null") return null;
      try {
        return JSON.parse(raw);
      } catch (e) {
        return String(raw).replace(/^"|"$/g, "");
      }
    };

    const parsedToken = getCurrentToken();
    if (!parsedToken) {
      // dummy socket when no token
      this.socket = {
        on: () => {},
        off: () => {},
        emit: () => {},
        connect: () => {},
        disconnect: () => {},
        removeAllListeners: () => {},
        connected: false,
      };
      return;
    }

    const backendUrl =
      process.env.REACT_APP_BACKEND_URL || "http://localhost:4000";
    let socketBase = backendUrl;
    let socketPath = "/socket.io/";

    try {
      const urlObj = new URL(backendUrl);
      if (urlObj.pathname && urlObj.pathname !== "/") {
        socketPath = `${urlObj.pathname.replace(/\/$/, "")}/socket.io/`;
        socketBase = `${urlObj.protocol}//${urlObj.host}`;
      }
    } catch (e) {
      // ignore malformed URL
    }

    this.isManualDisconnect = false;
    this.socket = io(`${socketBase}/${this?.company_id}`, {
      path: socketPath,
      autoConnect: true,
      transports: ["websocket", "polling"],
      upgrade: true,
      timeout: 10000,
      reconnection: true,
      reconnectionDelay: 2000,
      auth: { token: parsedToken },
    });

    // Refresh token on reconnection attempts
    if (this.socket && this.socket.io && this.socket.io.on) {
      this.socket.io.on("reconnect_attempt", () => {
        const freshToken = getCurrentToken();
        if (freshToken && this.socket) {
          this.socket.auth = { ...(this.socket.auth || {}), token: freshToken };
        }
      });
    }

    // Reconnect after server-initiated disconnect (e.g. token rejected)
    this.socket.on("disconnect", (reason) => {
      if (this.isManualDisconnect) return;
      if (reason === "io server disconnect") {
        // Server closed the connection; socket.io won't auto-reconnect.
        // Update auth with fresh token and reconnect after backoff.
        const freshToken = getCurrentToken();
        if (freshToken && this.socket) {
          this.socket.auth = { ...(this.socket.auth || {}), token: freshToken };
          setTimeout(() => {
            if (this.socket && !this.socket.connected && !this.isManualDisconnect) {
              this.socket.connect();
            }
          }, 3000);
        }
      }
    });
  }

  on(event, callback) {
    if (this.socket && typeof this.socket.on === "function") {
      this.socket.on(event, callback);
    }
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  emit(event, data) {
    if (this.socket && typeof this.socket.emit === "function") {
      this.socket.emit(event, data);
    }
  }

  off(event, callback) {
    if (this.socket && typeof this.socket.off === "function") {
      if (callback) {
        this.socket.off(event, callback);
      } else if (this.eventListeners[event]) {
        this.eventListeners[event].forEach((cb) => this.socket.off(event, cb));
      }
    }
    if (this.eventListeners[event]) {
      if (callback) {
        this.eventListeners[event] = this.eventListeners[event].filter(
          (cb) => cb !== callback
        );
      } else {
        delete this.eventListeners[event];
      }
    }
  }

  disconnect() {
    if (this.socket) {
      this.isManualDisconnect = true;
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      SocketWorker.instance = null;
    }
  }

  connect() {
    if (!this.socket) {
      this.configureSocket();
    } else if (!this.socket.connected) {
      this.socket.connect();
    }
  }

  forceReconnect() {
    if (this.socket) {
      this.isManualDisconnect = false;
      this.socket.disconnect();
      setTimeout(() => this.socket?.connect(), 250);
    } else {
      this.configureSocket();
    }
  }
}

const instance = (company_id, user_id) => new SocketWorker(company_id, user_id);
export default instance;
